/* eslint-disable @typescript-eslint/no-explicit-any */
import { CloseOutlined, FilterOutlined } from '@ant-design/icons'
import {
  DefinePaymentMethod,
  EPaymentMethod,
  IParamsGetReport24
} from 'app/api/report/model/report-24'
import R from 'app/assets/R'
import { SvgDownArrowIcon, SvgUpArrowIcon } from 'app/assets/svg-assets'
import { StatusEnum } from 'app/common/enum'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseDivider } from 'app/components/common/BaseDivider'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { BaseForm } from 'app/components/common/forms/BaseForm'
import { BaseDatePicker } from 'app/components/common/pickers/BaseDatePicker'
import { BaseSelect } from 'app/components/common/selects/BaseSelect'
import { notificationController } from 'app/controllers/notification-controller'
import { usePagination } from 'app/hook'
import { IClinic } from 'app/model/unit.model'
import { useGetAreaList } from 'app/react-query/hook/location'
import { convertedVariables } from 'app/styles/themes/themeVariables'
import { Dayjs } from 'dayjs'
import { t } from 'i18next'
import { Dates, FONT_SIZE, FONT_WEIGHT } from 'parkway-web-common'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DebtTreeSelect } from './components'
import { requestGetServices } from 'app/api/treatment-group'

interface IProps {
  onChangeFilter?: (value: IParamsGetReport24) => void
  filter?: IParamsGetReport24
}

export const AdvanceFilterLayout: React.FC<IProps> = ({
  filter,
  onChangeFilter,
}) => {
  const [open, setOpen] = useState(false)
  const [filterMore, setFilterMore] = useState<IParamsGetReport24 | undefined>(
    filter,
  )

  const { data: areaApi, isLoading: isLoadingArea } = useGetAreaList({
    typeid: 'clinic',
    status: StatusEnum.ACTIVE,
  })

  const { flattenDataList } = usePagination()

  const clinics: IClinic[] = useMemo(() => {
    return flattenDataList(areaApi) ?? []
  }, [areaApi])

  // action change
  const onChangeClinic = (patientDefaultLocations: string[]) => {
    setFilterMore?.({ ...filterMore, patientDefaultLocations })
  }

  const onChangeBeginOrthodonticStartDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.beginOrthodonticEndDate &&
      day.isAfter(filterMore?.beginOrthodonticEndDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      })
      return
    }

    const value = day ? day?.toISOString() : undefined
    setFilterMore?.(prev => ({ ...prev, beginOrthodonticStartDate: value }))
  }

  const onChangeBeginOrthodonticEndDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.beginOrthodonticStartDate &&
      day.isBefore(filterMore?.beginOrthodonticStartDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      })
      return
    }

    const value = day ? day?.toISOString() : undefined
    setFilterMore?.(prev => ({ ...prev, beginOrthodonticEndDate: value }))
  }

  // end orthodontic date
  const onChangeEndOrthodonticStartDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.endOrthodonticEndDate &&
      day.isAfter(filterMore?.endOrthodonticEndDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      })
      return
    }

    const value = day ? day?.toISOString() : undefined
    setFilterMore?.(prev => ({ ...prev, endOrthodonticStartDate: value }))
  }

  const onChangeEndOrthodonticEndDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.endOrthodonticStartDate &&
      day.isBefore(filterMore?.endOrthodonticStartDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      })
      return
    }

    const value = day ? day?.toISOString() : undefined
    setFilterMore?.(prev => ({ ...prev, endOrthodonticEndDate: value }))
  }

  const [keyword] = useState('')
  const [isTreatmentLoading, setIsTreatmentLoading] = useState(false)
  const [treatments, setTreatments] = useState<
    {
      name: string
    }[]
  >([])

  const getData = async (keyword: string) => {
    setIsTreatmentLoading(true)
    try {
      const response = await requestGetServices({ keyword, pageSize: 100 })
      setTreatments(response?.data)
    } catch (error) {
      return {
        data: [],
      }
    }
    setIsTreatmentLoading(false)
  }

  useEffect(() => {
    getData(keyword)
  }, [])

  const onHide = () => {
    setOpen(false)
  }

  const onPressApply = () => {
    onHide()
    onChangeFilter?.({
      ...filter,
      beginOrthodonticStartDate: filterMore?.beginOrthodonticStartDate,
      beginOrthodonticEndDate: filterMore?.beginOrthodonticEndDate,
      endOrthodonticStartDate: filterMore?.endOrthodonticStartDate,
      endOrthodonticEndDate: filterMore?.endOrthodonticEndDate,

      patientDefaultLocations: filterMore?.patientDefaultLocations,
      treatmentIds: filterMore?.treatmentIds,
      paymentMethods: filterMore?.paymentMethods,

      // Add the new filter fields:
      treatmentPlanStartDate: filterMore?.treatmentPlanStartDate,
      treatmentPlanEndDate: filterMore?.treatmentPlanEndDate,
      alignerPaymentStartDate: filterMore?.alignerPaymentStartDate,
      alignerPaymentEndDate: filterMore?.alignerPaymentEndDate,
      bracesEligibilityStartDate: filterMore?.bracesEligibilityStartDate,
      bracesEligibilityEndDate: filterMore?.bracesEligibilityEndDate,
      treatmentPlanFinalizedStartDate: filterMore?.treatmentPlanFinalizedStartDate,
      treatmentPlanFinalizedEndDate: filterMore?.treatmentPlanFinalizedEndDate,
    })
  }

  const onPressReset = () => {
    setFilterMore({})
    onChangeFilter?.({
      ...filter,
      patientDefaultLocations: [],
      beginOrthodonticStartDate: undefined,
      beginOrthodonticEndDate: undefined,
      endOrthodonticStartDate: undefined,
      endOrthodonticEndDate: undefined,
      buyOrthodonticsEndDate: undefined,
      buyOrthodonticsStartDate: undefined,
      keyword: undefined,
      treatmentIds: undefined,
      paymentMethods: undefined,
      // Add the new reset fields:
      treatmentPlanStartDate: undefined,
      treatmentPlanEndDate: undefined,
      alignerPaymentStartDate: undefined,
      alignerPaymentEndDate: undefined,
      bracesEligibilityStartDate: undefined,
      bracesEligibilityEndDate: undefined,
      treatmentPlanFinalizedStartDate: undefined,
      treatmentPlanFinalizedEndDate: undefined,
    })
  }

  const onChangeService = (treatmentIds: string[]) => {
    setFilterMore?.(prev => ({ ...prev, treatmentIds }))
  }

  const onChangePaymentMethod = (paymentMethods: EPaymentMethod[]) => {
    setFilterMore?.(prev => ({ ...prev, paymentMethods }))
  }

  // Add these functions inside your component, before the return statement:

  // Treatment Plan Date handlers
  const onChangeTreatmentPlanStartDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.treatmentPlanEndDate &&
      day.isAfter(filterMore?.treatmentPlanEndDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      });
      return;
    }

    const value = day ? day?.toISOString() : undefined;
    setFilterMore?.(prev => ({ ...prev, treatmentPlanStartDate: value }));
  };

  const onChangeTreatmentPlanEndDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.treatmentPlanStartDate &&
      day.isBefore(filterMore?.treatmentPlanStartDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      });
      return;
    }

    const value = day ? day?.toISOString() : undefined;
    setFilterMore?.(prev => ({ ...prev, treatmentPlanEndDate: value }));
  };

  // Aligner Payment Date handlers
  const onChangeAlignerPaymentStartDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.alignerPaymentEndDate &&
      day.isAfter(filterMore?.alignerPaymentEndDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      });
      return;
    }

    const value = day ? day?.toISOString() : undefined;
    setFilterMore?.(prev => ({ ...prev, alignerPaymentStartDate: value }));
  };

  const onChangeAlignerPaymentEndDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.alignerPaymentStartDate &&
      day.isBefore(filterMore?.alignerPaymentStartDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      });
      return;
    }

    const value = day ? day?.toISOString() : undefined;
    setFilterMore?.(prev => ({ ...prev, alignerPaymentEndDate: value }));
  };

  // Braces Eligibility Date handlers
  const onChangeBracesEligibilityStartDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.bracesEligibilityEndDate &&
      day.isAfter(filterMore?.bracesEligibilityEndDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      });
      return;
    }

    const value = day ? day?.toISOString() : undefined;
    setFilterMore?.(prev => ({ ...prev, bracesEligibilityStartDate: value }));
  };

  const onChangeBracesEligibilityEndDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.bracesEligibilityStartDate &&
      day.isBefore(filterMore?.bracesEligibilityStartDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      });
      return;
    }

    const value = day ? day?.toISOString() : undefined;
    setFilterMore?.(prev => ({ ...prev, bracesEligibilityEndDate: value }));
  };

  // Treatment Plan Finalized Date handlers
  const onChangeFinalizedStartDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.treatmentPlanFinalizedEndDate &&
      day.isAfter(filterMore?.treatmentPlanFinalizedEndDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      });
      return;
    }

    const value = day ? day?.toISOString() : undefined;
    setFilterMore?.(prev => ({ ...prev, treatmentPlanFinalizedStartDate: value }));
  };

  const onChangeFinalizedEndDate = (day?: Dayjs) => {
    if (
      day &&
      filterMore?.treatmentPlanFinalizedStartDate &&
      day.isBefore(filterMore?.treatmentPlanFinalizedStartDate)
    ) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      });
      return;
    }

    const value = day ? day?.toISOString() : undefined;
    setFilterMore?.(prev => ({ ...prev, treatmentPlanFinalizedEndDate: value }));
  };

  return (
    <BasePopoverWrapper
      trigger={'click'}
      placement="bottomRight"
      content={
        <BaseSpace
          split={<BaseDivider style={{ marginBottom: 0, marginTop: 0 }} />}
          size={12}
        >
          <BaseRow gutter={[16, 16]} align={'middle'} justify={'space-between'}>
            <BaseCol>
              <BaseText
                children={'Lọc thêm'}
                fontSize="xs"
                fontWeight="semibold"
              />
            </BaseCol>

            <BaseCol>
              <BaseButton
                icon={<CloseOutlined rev={undefined} />}
                onClick={onHide}
                type="text"
              />
            </BaseCol>
          </BaseRow>

          <BaseForm>
            <BaseSpace style={{ width: 450 }} size={4}>
              {/* // begin orthodontic date */}
              <BaseSpace size={0}>
                <BaseText
                  children={t(
                    R.strings.report_24_content_begin_orthodontic_date,
                  )}
                  fontWeight="semibold"
                  colorText="main2Color"
                />
                <BaseRow gutter={8} align={'middle'}>
                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        picker="date"
                        value={
                          filterMore?.beginOrthodonticStartDate
                            ? Dates.getDate(
                              filterMore?.beginOrthodonticStartDate,
                            )
                            : null
                        }
                        onChange={day =>
                          onChangeBeginOrthodonticStartDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.from_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>

                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        value={
                          filterMore?.beginOrthodonticEndDate
                            ? Dates.getDate(filterMore?.beginOrthodonticEndDate)
                            : null
                        }
                        picker="date"
                        onChange={day =>
                          onChangeBeginOrthodonticEndDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.to_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>
                </BaseRow>
              </BaseSpace>

              {/* // end orthodontic date */}
              <BaseSpace size={0}>
                <BaseText
                  children={t(R.strings.report_24_content_end_orthodontic_date)}
                  fontWeight="semibold"
                  colorText="main2Color"
                />
                <BaseRow gutter={8} align={'middle'}>
                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        picker="date"
                        value={
                          filterMore?.endOrthodonticStartDate
                            ? Dates.getDate(filterMore?.endOrthodonticStartDate)
                            : null
                        }
                        onChange={day =>
                          onChangeEndOrthodonticStartDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.from_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>

                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        value={
                          filterMore?.endOrthodonticEndDate
                            ? Dates.getDate(filterMore?.endOrthodonticEndDate)
                            : null
                        }
                        picker="date"
                        onChange={day =>
                          onChangeEndOrthodonticEndDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.to_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>
                </BaseRow>
              </BaseSpace>
              {/* // add new filter */}
              <BaseSpace size={0}>
                <BaseText
                  children={"Ngày lập phác đồ"}
                  fontWeight="semibold"
                  colorText="main2Color"
                />
                <BaseRow gutter={8} align={'middle'}>
                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        picker="date"
                        value={
                          filterMore?.treatmentPlanStartDate
                            ? Dates.getDate(filterMore?.treatmentPlanStartDate)
                            : null
                        }
                        onChange={day =>
                          onChangeTreatmentPlanStartDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.from_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>

                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        value={
                          filterMore?.treatmentPlanEndDate
                            ? Dates.getDate(filterMore?.treatmentPlanEndDate)
                            : null
                        }
                        picker="date"
                        onChange={day =>
                          onChangeTreatmentPlanEndDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.to_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>
                </BaseRow>
              </BaseSpace>
              <BaseSpace size={0}>
                <BaseText
                  children={"Ngày thanh toán sản xuất khay"}
                  fontWeight="semibold"
                  colorText="main2Color"
                />
                <BaseRow gutter={8} align={'middle'}>
                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        picker="date"
                        value={
                          filterMore?.alignerPaymentStartDate
                            ? Dates.getDate(filterMore?.alignerPaymentStartDate)
                            : null
                        }
                        onChange={day =>
                          onChangeAlignerPaymentStartDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.from_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>

                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        value={
                          filterMore?.alignerPaymentEndDate
                            ? Dates.getDate(filterMore?.alignerPaymentEndDate)
                            : null
                        }
                        picker="date"
                        onChange={day =>
                          onChangeAlignerPaymentEndDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.to_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>
                </BaseRow>
              </BaseSpace>
              <BaseSpace size={0}>
                <BaseText
                  children={"Ngày xác nhận đủ điều kiện gắn mắc cài"}
                  fontWeight="semibold"
                  colorText="main2Color"
                />
                <BaseRow gutter={8} align={'middle'}>
                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        picker="date"
                        value={
                          filterMore?.bracesEligibilityStartDate
                            ? Dates.getDate(filterMore?.bracesEligibilityStartDate)
                            : null
                        }
                        onChange={day =>
                          onChangeBracesEligibilityStartDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.from_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>

                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        value={
                          filterMore?.bracesEligibilityEndDate
                            ? Dates.getDate(filterMore?.bracesEligibilityEndDate)
                            : null
                        }
                        picker="date"
                        onChange={day =>
                          onChangeBracesEligibilityEndDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.to_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>
                </BaseRow>
              </BaseSpace>
              <BaseSpace size={0}>
                <BaseText
                  children={"Ngày chốt phác đồ"}
                  fontWeight="semibold"
                  colorText="main2Color"
                />
                <BaseRow gutter={8} align={'middle'}>
                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        picker="date"
                        value={
                          filterMore?.treatmentPlanFinalizedStartDate
                            ? Dates.getDate(filterMore?.treatmentPlanFinalizedStartDate)
                            : null
                        }
                        onChange={day =>
                          onChangeFinalizedStartDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.from_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>

                  <BaseCol xl={12}>
                    <BaseFormItem hiddenLabel>
                      <BaseDatePicker
                        value={
                          filterMore?.treatmentPlanFinalizedEndDate
                            ? Dates.getDate(filterMore?.treatmentPlanFinalizedEndDate)
                            : null
                        }
                        picker="date"
                        onChange={day =>
                          onChangeFinalizedEndDate(day as Dayjs)
                        }
                        allowClear
                        format={'DD-MM-YYYY'}
                        placeholder={t(R.strings.to_date)}
                      />
                    </BaseFormItem>
                  </BaseCol>
                </BaseRow>
              </BaseSpace>
              {/* // end add new filter */}
              <BaseFormItem
                label={t(R.strings.report_24_content_package_service)}
              >
                <DebtTreeSelect
                  isTreatmentLoading={isTreatmentLoading}
                  treatments={treatments}
                  selectedValues={filterMore?.treatmentIds ?? []}
                  setSelectedValues={onChangeService}
                  type={['service']}
                  placeholder={t(
                    R.strings.manage_customer_debt_choose_treatment,
                  )}
                />
              </BaseFormItem>

              <BaseFormItem label={t(R.strings.payment_method)}>
                <BaseSelectWrapper
                  mode="multiple"
                  showSearch
                  placeholder={t(R.strings.all)}
                  allowClear
                  onChange={methods =>
                    onChangePaymentMethod?.(methods as EPaymentMethod[])
                  }
                  value={filterMore?.paymentMethods}
                  options={[
                    {
                      label: DefinePaymentMethod?.[EPaymentMethod.FULL].text,
                      value: EPaymentMethod.FULL,
                    },
                    {
                      label:
                        DefinePaymentMethod?.[EPaymentMethod.INSTALLMENT].text,
                      value: EPaymentMethod.INSTALLMENT,
                    },
                    {
                      label:
                        DefinePaymentMethod?.[EPaymentMethod.INSTALLMENT_CREDIT]
                          .text,
                      value: EPaymentMethod.INSTALLMENT_CREDIT,
                    },
                    {
                      label:
                        DefinePaymentMethod?.[
                          EPaymentMethod.INSTALLMENT_FUNDIIN
                        ].text,
                      value: EPaymentMethod.INSTALLMENT_FUNDIIN,
                    },
                    {
                      label:
                        DefinePaymentMethod?.[EPaymentMethod.INSTALLMENT_PW]
                          .text,
                      value: EPaymentMethod.INSTALLMENT_PW,
                    },
                    {
                      label:
                        DefinePaymentMethod?.[EPaymentMethod.INSTALLMENT_HOME_CREDIT]
                          .text,
                      value: EPaymentMethod.INSTALLMENT_HOME_CREDIT,
                    },
                  ]}
                  maxTagCount={0}
                />
              </BaseFormItem>

              <BaseFormItem label={'Cơ sở'}>
                <BaseSelectWrapper
                  mode="multiple"
                  showSearch
                  placeholder={t(R.strings.all)}
                  allowClear
                  onChange={ids => onChangeClinic?.(ids as string[])}
                  options={clinics?.map(item => ({
                    value: item.oldid,
                    label: item.name,
                  }))}
                  loading={isLoadingArea}
                  maxTagCount={0}
                  value={filterMore?.patientDefaultLocations}
                />
              </BaseFormItem>
            </BaseSpace>
          </BaseForm>

          <BaseRow gutter={[16, 16]} align={'middle'} justify={'space-between'}>
            <BaseCol>
              <BaseButton
                onClick={onPressReset}
                children={t(R.strings.report_24_content_reset)}
              />
            </BaseCol>

            <BaseCol>
              <BaseButton
                onClick={onPressApply}
                type="primary"
                children={t(R.strings.report_24_content_apply)}
              />
            </BaseCol>
          </BaseRow>
        </BaseSpace>
      }
      open={open}
      onOpenChange={setOpen}
    >
      <ButtonOpen
        children={
          <BaseRow align={'middle'} justify={'center'} gutter={[16, 8]}>
            <BaseCol>
              <FilterOutlined rev={undefined} />
            </BaseCol>
            <BaseCol>
              <BaseText
                children={'Lọc thêm'}
                fontWeight="medium"
                fontSize="xs"
                opacity="0.7"
              />
            </BaseCol>
            {!open ? (
              <SvgDownArrowIcon style={{ width: 20, height: 20 }} />
            ) : (
              <SvgUpArrowIcon style={{ width: 20, height: 20 }} />
            )}
          </BaseRow>
        }
      />
    </BasePopoverWrapper>
  )
}

const BaseFormItem = styled(BaseForm.Item)`
  margin-bottom: 0px !important;
`

const BaseSelectWrapper = styled(BaseSelect)`
  width: 100%;
  .ant-select-selection-placeholder {
    padding-left: ${props =>
    props.$prefixWidth ? `${props?.$prefixWidth}px` : 0};
  }

  .ant-select-selection-item {
    font-size: ${FONT_SIZE.xs};
    font-weight: ${FONT_WEIGHT.medium};
    color: ${convertedVariables.primaryColor};
    opacity: 0.9;
  }
`

const ButtonOpen = styled(BaseButton)`
  border-color: #f0f1f3;

  &:hover {
    .anticon {
      color: ${convertedVariables.primaryColor};
      opacity: 0.7;
    }
  }
`

const BasePopoverWrapper = styled(BasePopover)``
